import { AnalyticsEventName } from '@dupe/common/constants'
import { pruneStaleMetaConversionMarks } from './metaConversions'

/**
 * Meta's SERVER-side twin of `Performed Dupe` (DUP-2091 → PostHog CAPI).
 * Shared by both call sites (`useTrackPerformedDupe.ts`, `BrainChat.tsx`) so
 * the firing rule and its dedup live in one place, the way
 * `googleAdsConversions.ts`'s `fireOnce` does for Google.
 *
 * Runs ALONGSIDE the browser pixel (`metaConversions.ts`), not instead of it.
 * Two transports for one conversion is Meta's own recommendation: the pixel
 * carries `fbp`/`fbc` and the real client IP/UA that a server call cannot
 * see, while the server call survives ad blockers and ITP. They are made safe
 * by a shared `event_id` — see below.
 *
 * Fires `AnalyticsEventName.PerformedDupeAdsConversion` to PostHog only
 * (`skipGA: true`), stamping `event_id` with the same id the pixel puts in its
 * `eventID` — which since 2026-09 is a per-user-per-DAY id, not a per-search
 * one (`dailyConversionId`, shared with TikTok since DUP-2261). Both Meta transports therefore cap together;
 * if only one were capped the pair would stop matching and Meta would count
 * the uncapped one separately. Meta collapses repeats of one event
 * name + `event_id`, so the pixel copy and the CAPI copy of a search resolve
 * to ONE conversion rather than two. `googleAdsConversions.ts` stamps the
 * same id for the same reason on its own platforms.
 *
 * ⚠️ The destination must be told to READ that property — it is not
 * automatic. Its `eventId` input currently holds the schema default
 * `{event.uuid}`, which would give every send a fresh random id and defeat
 * the dedup silently. Repointing it is a pre-launch gate (see
 * docs/features/active/zaraz-migration/TODO.md); `eventId` is a TOP-LEVEL
 * input shared by every mapping, so the expression needs a fallback
 * (`{event.properties.event_id ?? event.uuid}`) or the `User Signed In`
 * mapping sends an empty required field.
 *
 * ⚠️ Nothing here reaches Meta TODAY. Verified against the live config on
 * 2026-09-03 (destination `01a06853-8af9-0000-80b4-921ace0e5c88`): it is
 * `enabled: false`, has never executed, and its trigger still names
 * `Performed Dupe`, not this event. Do not read this comment as a description
 * of production.
 *
 * ⚠️ And before enabling it, know that this would be Meta's THIRD transport
 * for this conversion, not its second. Verified in Events Manager the same
 * day: a Stape Conversions API Gateway (`capig.stape.gl`) is already live and
 * mirrors the pixel's `Performed Dupe` server-side at ~1:1. Stape's copy
 * carries the browser's `fbp`/`fbc`/IP; this one would carry a user agent and
 * nothing else on ~85% of sends. Whether that is worth sending at all is
 * Gate 0 in docs/features/active/zaraz-migration/TODO.md — an open product
 * question, not a settled plan.
 *
 * ✅ The destination's 86400s masking throttle (person + event + date) used to
 * be in tension with the pixel, which fired per search. It no longer is: since
 * DUP-2188 the pixel is capped at one per user per day, which is exactly the
 * throttle's grain. Both sides now agree, so a paired send is genuinely one
 * conversion rather than one of three.
 *
 * The guard below is cheap per-search hygiene and a backstop that does not
 * depend on the destination staying configured the way it is today.
 *
 * ⚠️ It is NOT as strong as its two siblings, and the difference is easy to
 * miss — two separate reviewers have now read it as equivalent. Both of the
 * others check a real delivery signal before marking a conversion sent:
 * `googleAdsConversions.ts` gates on `zarazTrack`'s boolean return, and
 * `metaConversions.ts` gates on `window.fbq` existing. This one has none,
 * because `useAnalytics().track` discards `posthog.capture`'s result — and
 * that result is typed `CaptureResult | undefined`, meaning a silent no-op
 * (SDK not loaded, opted out, rate-limited, dropped by `before_send`) is a
 * NORMAL outcome that does not throw. So a search can be marked fired for
 * the rest of the session having reached nothing.
 *
 * ⚠️ DUP-2188 made this WORSE, and the gate should be re-weighed before the
 * destination is enabled. The mark used to live in `sessionStorage` under a
 * per-search key, so a silent no-op cost one search in one tab and the next
 * tab retried. It now lives in `localStorage` under a per-DAY key: a single
 * no-op — PostHog not yet loaded on a cold first paint, opted out, client
 * rate-limited, dropped by `before_send` — suppresses that browser's ONLY
 * Meta CAPI conversion for the whole day, across every tab and a restart,
 * with no retry.
 *
 * Impact is still nil today (the destination delivers nowhere), and the fix is
 * not local to this file — `track` would have to surface the capture result.
 * See Gate 6 in docs/features/active/zaraz-migration/TODO.md. Do not
 * "simplify" this comment away on the assumption it mirrors `fireOnce`; it
 * doesn't.
 */

const firedConversions = new Set<string>()
const STORAGE_PREFIX = 'meta-ads-conversion:'

const hasFired = (key: string): boolean => {
  if (firedConversions.has(key)) return true

  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${key}`) !== null
  } catch {
    // localStorage can be unavailable in privacy-restricted browsers. The
    // module-level set still prevents duplicate React effects in that case.
    return false
  }
}

const markFired = (key: string): void => {
  firedConversions.add(key)
  // This module's keys live in `localStorage` too, so they need the same
  // sweep. The helper is imported rather than reimplemented — a second copy
  // would drift, and a prefix missing from one of them grows silently.
  pruneStaleMetaConversionMarks()

  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, `${Date.now()}`)
  } catch {
    // See hasFired: privacy-restricted browsers throw here.
  }
}

/** The subset of `useAnalytics().track` this module needs — accepted as a
 *  parameter (dependency injection) rather than imported, since `track`
 *  itself is a React hook's return value and this module isn't one. */
type TrackFn = (
  event: string,
  properties?: Record<string, unknown>,
  options?: Record<string, unknown>
) => void

export const trackMetaAdsConversion = (
  track: TrackFn,
  {
    transactionId,
    ...properties
  }: { transactionId: string } & Record<string, unknown>
): void => {
  // Keyed by event name AND transaction id, matching `googleAdsConversions.ts`
  // and `metaConversions.ts`. Only one event name flows through here today, so
  // the prefix is redundant right now — it is here so that adding a second
  // conversion later (the Google module already carries account creation)
  // cannot have it silently swallowed as a duplicate of the search conversion
  // that happens to share a transaction id.
  const key = `${AnalyticsEventName.PerformedDupeAdsConversion}:${transactionId}`
  if (hasFired(key)) return

  // A throw is the ONLY failure this can currently see — `useAnalytics().track`
  // returns nothing, discarding the `CaptureResult | undefined` that
  // `posthog.capture` hands back. Marking only on a non-throwing call keeps a
  // hard failure retryable on the next search, but does NOT catch PostHog's
  // normal failure mode, which is returning `undefined` without throwing. See
  // the module doc for why that gap is not closed here.
  try {
    // `event_id` last: it is both the key this function deduped on and what
    // Meta pairs the pixel copy against, so a caller's extras must never be
    // able to overwrite it. Mirrors `metaConversions.ts`'s `transaction_id`.
    track(
      AnalyticsEventName.PerformedDupeAdsConversion,
      { ...properties, event_id: transactionId },
      { skipGA: true }
    )
  } catch {
    return
  }

  markFired(key)
}
